/**
 * backend-direct-client.ts
 * 
 * Direct client for communicating with the Kubernetes AWS backend.
 * This bypasses Vercel serverless functions for memory-intensive operations.
 */

import axios from 'axios';

// Define our own AxiosRequestConfig interface since the import is failing
interface RequestConfig {
  timeout?: number;
  headers?: Record<string, string>;
  [key: string]: any;
}

// Kubernetes AWS backend URL - using HTTPS for secure communication
const BACKEND_URL = process.env.BACKEND_API_URL || 'https://stewie.api.clewis.me';

/**
 * BackendDirectClient provides direct access to the Kubernetes AWS backend
 * to avoid the memory constraints of serverless functions.
 */
export class BackendDirectClient {
  private baseUrl: string;
  private defaultTimeout: number;

  constructor(baseUrl = BACKEND_URL, defaultTimeout = 60000) {
    this.baseUrl = baseUrl;
    this.defaultTimeout = defaultTimeout;
  }

  /**
   * Check if the backend is available
   */
  async checkHealth(): Promise<{ status: string }> {
    const endpoint = '/health';
    const url = `${this.baseUrl}${endpoint}`;
    
    console.log(`[BackendDirectClient] Checking backend health: ${url}`);
    
    try {
      // Remove the User-Agent header which browsers won't allow to be set
      // Browsers automatically set their own User-Agent and restrict modification
      const response = await axios.get(url, {
        headers: {
          'Content-Type': 'application/json'
          // User-Agent removed to prevent 'Refused to set unsafe header "User-Agent"' error
        },
        timeout: 5000 // Quick timeout for health check
      });
      
      console.log(`[BackendDirectClient] Backend health check response: ${response.status}`);
      return { status: 'available' };
    } catch (error: any) {
      console.error(`[BackendDirectClient] Backend health check failed:`, error.message);
      return { status: 'unavailable' };
    }
  }

  /**
   * Run a backtest directly against the Kubernetes AWS backend
   */
  async runBacktest(backtestRequest: any): Promise<any> {
    const endpoint = '/api/backtest/run';
    const url = `${this.baseUrl}${endpoint}`;
    
    // Ensure provider is set, defaulting to alpaca if not specified
    const provider = backtestRequest?.settings?.provider || 'alpaca';
    console.log(`[BackendDirectClient] Sending backtest request directly to backend using provider: ${provider}`);
    
    // Ensure the provider is explicitly set in the request settings
    if (backtestRequest && backtestRequest.settings) {
      backtestRequest.settings.provider = provider;
    }
    
    try {
      const response = await axios.post(url, backtestRequest, {
        headers: {
          'Content-Type': 'application/json',
          'X-Preferred-Provider': provider // Add explicit header for provider preference
        },
        timeout: this.defaultTimeout
      });
      
      console.log(`[BackendDirectClient] Received successful response from backend`);
      return response.data;
    } catch (error: any) {
      console.error(`[BackendDirectClient] Error running backtest:`, error.message);
      throw error;
    }
  }

  /**
   * Fetch historical data directly from the Kubernetes AWS backend
   */
  async getHistoricalData(
    symbols: string[], 
    startDate: string, 
    endDate: string, 
    timeframe = 'day', 
    provider = 'alpaca'
  ): Promise<any> {
    const endpoint = '/api/backtest/historical-data';
    const url = `${this.baseUrl}${endpoint}`;
    
    console.log(`[BackendDirectClient] Fetching historical data directly from backend for ${symbols.length} symbols using provider: ${provider}`);
    
    try {
      const response = await axios.post(url, {
        symbols,
        startDate,
        endDate,
        timeframe,
        provider, // Use provided provider
        isPaper: true,
        fillGaps: true
      }, {
        headers: {
          'Content-Type': 'application/json',
          'X-Preferred-Provider': provider
        },
        timeout: this.defaultTimeout
      });
      
      return response.data;
    } catch (error: any) {
      console.error(`[BackendDirectClient] Error fetching historical data:`, error.message);
      throw error;
    }
  }

  /**
   * Fetch comprehensive data with validation directly from the Kubernetes AWS backend
   */
  async getComprehensiveData(
    symbols: string[], 
    startDate: string, 
    endDate: string, 
    timeframe = 'day',
    provider = 'alpaca'
  ): Promise<any> {
    const endpoint = '/api/backtest/comprehensive-data';
    const url = `${this.baseUrl}${endpoint}`;
    
    console.log(`[BackendDirectClient] Fetching comprehensive data directly from backend for ${symbols.length} symbols using provider: ${provider}`);
    
    try {
      const response = await axios.post(url, {
        symbols,
        startDate,
        endDate,
        timeframe,
        provider, // Use provided provider
        isPaper: true,
        fillGaps: true
      }, {
        headers: {
          'Content-Type': 'application/json',
          'X-Preferred-Provider': provider
        },
        timeout: this.defaultTimeout
      });
      
      return response.data;
    } catch (error: any) {
      console.error(`[BackendDirectClient] Error fetching comprehensive data:`, error.message);
      throw error;
    }
  }

  /**
   * Generic method to call any backend endpoint directly
   */
  async callEndpoint(endpoint: string, method = 'GET', data?: any, config?: RequestConfig): Promise<any> {
    const url = `${this.baseUrl}${endpoint}`;
    
    try {
      const requestConfig: RequestConfig = {
        ...config,
        timeout: config?.timeout || this.defaultTimeout,
        headers: {
          'Content-Type': 'application/json',
          ...(config?.headers || {})
        }
      };
      
      if (method.toUpperCase() === 'GET') {
        return await axios.get(url, requestConfig);
      } else if (method.toUpperCase() === 'POST') {
        return await axios.post(url, data, requestConfig);
      } else if (method.toUpperCase() === 'PUT') {
        return await axios.put(url, data, requestConfig);
      } else if (method.toUpperCase() === 'DELETE') {
        return await axios.delete(url, requestConfig);
      } else {
        throw new Error(`Unsupported HTTP method: ${method}`);
      }
    } catch (error: any) {
      console.error(`[BackendDirectClient] Error calling ${method} ${endpoint}:`, error.message);
      throw error;
    }
  }
}

// Export singleton instance
export const backendDirectClient = new BackendDirectClient();

// Keep the old export name for backward compatibility during transition
export const linodeDirectClient = backendDirectClient;

export default backendDirectClient; 